/**
 * WatchedWalletsList — F-035
 *
 * Page-Komponente fuer Route /wallets/watched.
 * Liste aller beobachteten Wallets (ohne Ownership).
 * Aelnelich WalletsList aber:
 * - Zeigt "Watched Wallets" Header statt "My Wallets"
 * - "Wallet beobachten"-Button fuehrt zu Address-Input (kein Ownership-Flow)
 * - Kein Disconnect-Button (nur Entfernen aus der Watchlist)
 * - Lese-Zugriff auf Rewards/Stats der fremden Wallet
 */
import { useState, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation, Trans } from 'react-i18next'
import { useWatchedWallets } from './useWatchedWallets'
import { addWatchedWallet, disconnectWallet, setWalletNotify } from './api/walletApi'
import { truncateAddress, hashToGradient } from './walletUtils'
import type { Wallet } from './useWallets'
import { fmt, fmtPrice } from '../../lib/fmt'
import { useCryptoPrices } from '../../hooks/useCryptoPrices'
import { toast } from '../../components/Toast/useToast'
import { apiErrorMessage } from '../../lib/errors/apiErrorMessage'

/**
 * heliumtracker-web#19: BE liefert `balance`/`balance_iot`/`balance_mobile` fuer
 * ALLE Wallets ueber denselben Endpoint GET /api/v2/wallets — unabhaengig von
 * `owned` (api/v2/wallets_controller.rb#index, kein Branching auf die Balance-
 * Felder). Der fruehere Kommentar "Watched Wallets liefern KEINE Balance-Felder"
 * war falsch bzw. veraltet. Analog zu WalletsList.tsx (WalletListItem) lesen wir
 * die Felder ueber einen lokal erweiterten Typ, damit sie erforderlich (nicht
 * optional) und damit typsicher gegen BE-Regressionen sind.
 */
type WatchedWalletListItem = Wallet & {
  balance: number
  balance_iot: number
  balance_mobile: number
  // notify: PATCH /api/v2/wallets/{id} { wallet: { notify } } ist real im
  // Contract (api-types updateApiV2Wallet), aber das Wallet-Response-Schema
  // deklariert das Feld nicht (gleiche Luecke wie bei den balance_*-Feldern
  // oben) — daher lokal optional statt required.
  notify?: boolean
}

// --- Add Watched Wallet Modal ---

type AddWatchedWalletModalProps = {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

function AddWatchedWalletModal({ isOpen, onClose, onSuccess }: AddWatchedWalletModalProps) {
  const { t } = useTranslation('wallets')
  const [address, setAddress] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!address.trim()) return

    setIsLoading(true)
    setError(null)

    try {
      const result = await addWatchedWallet(address.trim())
      const status = result.response?.status

      if (status === 201 || status === 200) {
        setAddress('')
        onSuccess()
      } else if (status === 409) {
        setError(t('error.alreadyWatched'))
      } else if (status === 422) {
        // onboarding-wallet-hotspot Finding #10: spezifische statt generische
        // Fehlermeldung bei ungültiger Solana-Adresse (statt "Ein Fehler ist
        // aufgetreten" / "Fehler beim Hinzufügen").
        setError(t('error.invalidAddress'))
      } else {
        setError(t('error.watchlistError'))
      }
    } catch {
      setError(t('error.genericError'))
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={t('watchedModal.label')}>
      <div className="card modal-card">
        <h3>{t('watchedModal.title')}</h3>
        <p className="muted" style={{ marginTop: 8, marginBottom: 16 }}>
          {t('watchedModal.description')}
        </p>
        <form onSubmit={handleSubmit}>
          <input
            className="input"
            type="text"
            placeholder={t('watchedModal.placeholder')}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            style={{ width: '100%', marginBottom: 12 }}
            disabled={isLoading}
          />
          {error && (
            <div className="hint c-danger" role="alert" style={{ marginBottom: 12 }}>
              {error}
            </div>
          )}
          <div className="wallet-connect-actions">
            <button type="submit" className="btn btn-primary" disabled={isLoading || !address.trim()}>
              {isLoading ? t('actions.addingToWatchlist') : t('actions.addToWatchlist')}
            </button>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={isLoading}>
              {t('common:actions.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// --- Loading Skeleton ---

function SkeletonRow() {
  return (
    <div className="list-row skeleton" aria-hidden="true" style={{ gridTemplateColumns: 'minmax(240px,1.4fr) 220px 1fr 240px auto' }}>
      <div className="identity">
        <div className="identity-img identity-img-sm" style={{ background: '#ddd', borderRadius: '50%' }} />
        <div className="identity-meta">
          <div className="skeleton-line" style={{ width: 120, height: 14 }} />
          <div className="skeleton-line" style={{ width: 80, height: 12, marginTop: 4 }} />
        </div>
      </div>
      <div className="skeleton-line" style={{ width: 80, height: 14 }} />
      <div className="skeleton-line" style={{ width: 100, height: 14 }} />
      <div className="skeleton-line" style={{ width: 80, height: 14 }} />
    </div>
  )
}

function LoadingSkeleton() {
  const { t } = useTranslation('wallets')
  return (
    <div aria-busy="true" aria-label={t('loading.watchedWallets')}>
      {Array.from({ length: 3 }).map((_, i) => (
        <SkeletonRow key={i} />
      ))}
    </div>
  )
}

// --- Error State ---

type ErrorStateProps = { onRetry: () => void }

function ErrorState({ onRetry }: ErrorStateProps) {
  const { t } = useTranslation('wallets')
  return (
    <div className="error-state" role="alert">
      <p>{t('error.loadWatchedWallets')}</p>
      <button className="btn btn-primary" onClick={onRetry}>
        {t('common:actions.retry')}
      </button>
    </div>
  )
}

// --- Empty State ---

type EmptyStateProps = { onAdd: () => void }

function EmptyState({ onAdd }: EmptyStateProps) {
  const { t } = useTranslation('wallets')
  return (
    <div className="empty-state">
      <p>{t('empty.noWatchedWallets')}</p>
      <button className="btn btn-primary" onClick={onAdd} data-testid="btn-add-watched-empty">
        {t('actions.watchWallet')}
      </button>
    </div>
  )
}

// --- Confirm-Dialog fuer Entfernen ---

type RemoveConfirmProps = {
  wallet: Wallet
  onConfirm: () => void
  onCancel: () => void
  isLoading: boolean
}

function RemoveConfirmDialog({ wallet, onConfirm, onCancel, isLoading }: RemoveConfirmProps) {
  const { t } = useTranslation('wallets')
  const shortAddr = truncateAddress(wallet.sol_addr ?? '')
  const displayName = wallet.name ?? shortAddr

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t('removeWatched.dialogLabel')}
      data-testid="remove-confirm-dialog"
    >
      <div className="card modal-card">
        <p>
          {/* Echter Dialog (kein window.confirm) → vorhandenen HTML-Key via <Trans> verdrahten,
              statt eine Plain-Variante zu duplizieren (s. Story i18n100-1). */}
          <Trans
            i18nKey="removeWatched.confirmMessage"
            ns="wallets"
            values={{ name: displayName }}
            components={{ strong: <strong /> }}
          />
        </p>
        <div className="wallet-connect-actions">
          <button
            className="btn btn-danger"
            onClick={onConfirm}
            disabled={isLoading}
            data-testid="btn-confirm-remove"
            aria-busy={isLoading}
          >
            {isLoading ? t('actions.removing') : t('actions.removeFromWatchlist')}
          </button>
          <button
            className="btn btn-ghost"
            onClick={onCancel}
            disabled={isLoading}
            data-testid="btn-cancel-remove"
          >
            {t('common:actions.cancel')}
          </button>
        </div>
      </div>
    </div>
  )
}

// --- Page Header ---
// Design-Referenz watched-wallets.html hat KEIN Hero-Card (anders als wallets.html):
// nur H1 + Beschreibungszeile + Button, rechtsbuendig.

type PageHeaderProps = {
  onOpenModal?: () => void
}

function PageHeader({ onOpenModal }: PageHeaderProps) {
  const { t } = useTranslation('wallets')
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
      <div>
        <h1 style={{ margin: '0 0 4px', fontSize: '22px' }}>{t('header.watchedWalletsTitle')}</h1>
        <div className="muted" style={{ fontSize: '13px', maxWidth: '560px' }}>
          {t('header.watchedWalletsDescription')}
        </div>
      </div>
      <button className="btn btn-primary" onClick={onOpenModal}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: '6px' }}>
          <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M7 4V7L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        {t('actions.watchWallet')}
      </button>
    </div>
  )
}

// --- Filter Bar ---

type FilterBarProps = {
  searchQuery: string
  onSearchChange: (query: string) => void
}

function FilterBar({ searchQuery, onSearchChange }: FilterBarProps) {
  const { t } = useTranslation('wallets')
  return (
    <div className="filterbar" style={{ marginBottom: '14px' }}>
      <div style={{ position: 'relative' }}>
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-subtle)' }}
        >
          <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M9.5 9.5L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <input
          className="input search-input"
          placeholder={t('filter.searchWatchedPlaceholder')}
          style={{ paddingLeft: '32px', minWidth: '240px' }}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>
    </div>
  )
}

// --- Watched Wallet Row ---

type WatchedWalletRowProps = {
  wallet: Wallet
  index: number
  onRemove: (wallet: Wallet) => void
  notifyOn: boolean
  onNotifyToggle: (wallet: Wallet, next: boolean) => void
}

function WatchedWalletRow({ wallet, index, onRemove, notifyOn, onNotifyToggle }: WatchedWalletRowProps) {
  const { t } = useTranslation('wallets')
  const navigate = useNavigate()
  const { prices, currency } = useCryptoPrices()
  const hntPrice = prices.hnt ?? 0
  const iotPrice = prices.iot ?? 0
  const mobilePrice = prices.mobile ?? 0
  const gradient = hashToGradient(wallet.sol_addr ?? '')
  const shortAddr = truncateAddress(wallet.sol_addr ?? '')
  const displayName = wallet.name ?? shortAddr

  // heliumtracker-web#19: BE liefert balance/balance_iot/balance_mobile auch fuer
  // watched Wallets (siehe WatchedWalletListItem oben) — v1-Paritaet.
  const item = wallet as WatchedWalletListItem
  const hntBalance = item.balance
  const iotBalance = item.balance_iot
  const mobileBalance = item.balance_mobile

  const tones = ['tone-1', 'tone-3', 'tone-6', 'tone-2']
  const tone = tones[index % 4]

  function handleClick(e: React.MouseEvent) {
    e.preventDefault()
    navigate({ to: '/wallets/$id', params: { id: String(wallet.id) } })
  }

  return (
    <div
      className="list-row"
      style={{
        // flex statt grid (List-row-Grid der Foundation ist nicht responsiv) —
        // so wrappen die 4 Bloecke bei 390px auf mehrere Zeilen statt
        // horizontal zu scrollen, ohne components.css anzufassen.
        display: 'flex',
        flexWrap: 'wrap',
        rowGap: '10px',
        columnGap: '16px',
        textDecoration: 'none',
        cursor: 'pointer',
      }}
      onClick={handleClick}
      role="link"
      aria-label={t('row.walletLabel', { name: displayName })}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          navigate({ to: '/wallets/$id', params: { id: String(wallet.id) } })
        }
      }}
    >
      {/* Identity */}
      <div className="identity" style={{ flex: '1 1 200px', minWidth: 0 }}>
        <div
          className={`identity-img ${tone}`}
          style={{ background: gradient }}
          aria-hidden="true"
        >
          <svg width="18" height="18" viewBox="0 0 14 14" fill="none">
            <rect x="1" y="3" width="12" height="9" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
            <path d="M4 3V2C4 1.44772 4.44772 1 5 1H9C9.55228 1 10 1.44772 10 2V3" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </div>
        <div className="identity-meta">
          <div className="identity-title">
            {displayName}
          </div>
          <div className="identity-sub">
            <span className="addr-chip">
              {shortAddr}
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ marginLeft: '4px' }}>
                <rect x="0.5" y="2" width="9" height="6" rx="1" stroke="currentColor" strokeWidth="1" fill="none" />
                <path d="M3 5H7M5 4V6" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
              </svg>
            </span>
            <span>&#183;</span>
            <span>{t('row.hotspots', { count: wallet.hotspot_count ?? 0 })}</span>
          </div>
        </div>
      </div>

      {/* Token amounts — v1-Paritaet: HNT, EUR, IOT, MOBILE pro Zeile (heliumtracker-web#19) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 1 220px' }}>
        <div className="row" style={{ gap: '6px' }}>
          <span className="token-mark t-hnt">H</span>
          <span className="num-sm c-hnt" style={{ fontWeight: 600 }}>{fmt(hntBalance)}</span>
          <span className="muted" style={{ fontSize: '11px' }}>HNT</span>
          <span className="muted" style={{ fontSize: '11px' }}>≈ {fmtPrice(hntBalance * hntPrice, currency)}</span>
        </div>
        {iotBalance > 0 && (
          <div className="row" style={{ gap: '6px' }}>
            <span className="token-mark t-iot">I</span>
            <span className="num-sm c-iot" style={{ fontWeight: 600 }}>{fmt(iotBalance)}</span>
            <span className="muted" style={{ fontSize: '11px' }}>IOT</span>
            <span className="muted" style={{ fontSize: '11px' }}>≈ {fmtPrice(iotBalance * iotPrice, currency)}</span>
          </div>
        )}
        {mobileBalance > 0 && (
          <div className="row" style={{ gap: '6px' }}>
            <span className="token-mark t-mobile">M</span>
            <span className="num-sm c-mobile" style={{ fontWeight: 600 }}>{fmt(mobileBalance)}</span>
            <span className="muted" style={{ fontSize: '11px' }}>MOBILE</span>
            <span className="muted" style={{ fontSize: '11px' }}>≈ {fmtPrice(mobileBalance * mobilePrice, currency)}</span>
          </div>
        )}
      </div>

      {/* Notify-on-change toggle — design: watched-wallets.html .switch/.is-on */}
      <label
        className="row"
        // padding statt fixer switch-Groesse: haelt den 36x20-Look der Design-
        // Referenz, aber die Tap-Flaeche (Label + Padding) erreicht >=44px
        // Hoehe fuer mobile Hit-Targets (ACCEPTANCE-Gate).
        style={{ flex: '0 0 auto', gap: '8px', fontSize: '12px', color: 'var(--text-muted)', cursor: 'pointer', padding: '12px 4px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <span
          role="switch"
          aria-checked={notifyOn}
          aria-label={t('notify.toggleLabel', { name: displayName })}
          tabIndex={0}
          data-testid={`notify-toggle-${wallet.id}`}
          className={`switch${notifyOn ? ' is-on' : ''}`}
          onClick={() => onNotifyToggle(wallet, !notifyOn)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onNotifyToggle(wallet, !notifyOn)
            }
          }}
        />
        <span style={{ minWidth: '24px' }}>{notifyOn ? t('notify.on') : t('notify.off')}</span>
      </label>

      {/* Row actions: open (read-only) + unwatch */}
      <div className="row" style={{ flex: '0 0 auto', color: 'var(--text-subtle)', gap: '4px' }}>
        <button
          className="btn btn-ghost btn-icon"
          data-testid={`btn-open-${wallet.id}`}
          onClick={(e) => {
            e.stopPropagation()
            navigate({ to: '/wallets/$id', params: { id: String(wallet.id) } })
          }}
          aria-label={t('row.openWallet', { name: displayName })}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 7C1 7 3.5 2.5 7 2.5C10.5 2.5 13 7 13 7C13 7 10.5 11.5 7 11.5C3.5 11.5 1 7 1 7Z" stroke="currentColor" strokeWidth="1.3" fill="none" />
            <circle cx="7" cy="7" r="2" stroke="currentColor" strokeWidth="1.3" fill="none" />
          </svg>
        </button>
        <button
          className="btn btn-ghost btn-icon"
          data-testid={`btn-remove-${wallet.id}`}
          onClick={(e) => {
            e.stopPropagation()
            onRemove(wallet)
          }}
          aria-label={t('removeWatched.walletLabel', { name: displayName })}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  )
}

// --- Main Component ---

export function WatchedWalletsList() {
  const { t } = useTranslation('wallets')
  const navigate = useNavigate()
  const { wallets, isLoading, isError, refetch } = useWatchedWallets()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [removeTarget, setRemoveTarget] = useState<Wallet | null>(null)
  const [isRemoving, setIsRemoving] = useState(false)
  const [removeError, setRemoveError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  // Notify-Overrides: optimistisches UI ueber PATCH /api/v2/wallets/{id}
  // {wallet:{notify}} (real im Contract) — `notify` ist im GET-Response-Schema
  // nicht deklariert, Default daher `true` bis der Server etwas anderes sagt.
  const [notifyOverrides, setNotifyOverrides] = useState<Record<number, boolean>>({})

  // Watched Wallets haben keine Balance-Felder — keine Fake-Summen berechnen.

  // Filter wallets by search
  const filteredWallets = useMemo(() => {
    if (!wallets) return []
    if (!searchQuery) return wallets
    const query = searchQuery.toLowerCase()
    return wallets.filter((w) =>
      (w.name?.toLowerCase().includes(query)) ||
      (w.sol_addr?.toLowerCase().includes(query) ?? false)
    )
  }, [wallets, searchQuery])

  function handleOpenModal() {
    setIsModalOpen(true)
  }

  function handleCloseModal() {
    setIsModalOpen(false)
  }

  function handleAddSuccess() {
    setIsModalOpen(false)
    refetch()
  }

  function handleRemoveClick(wallet: Wallet) {
    setRemoveTarget(wallet)
    setRemoveError(null)
  }

  async function handleRemoveConfirm() {
    if (!removeTarget) return
    setIsRemoving(true)

    try {
      const result = await disconnectWallet(Number(removeTarget.id))

      if (result.response?.status === 204 || result.response?.ok) {
        setRemoveTarget(null)
        refetch()
      } else {
        setRemoveError(t('error.removeError'))
      }
    } catch {
      setRemoveError(t('error.removeError'))
    } finally {
      setIsRemoving(false)
    }
  }

  function handleRemoveCancel() {
    setRemoveTarget(null)
    setRemoveError(null)
  }

  async function handleNotifyToggle(wallet: Wallet, next: boolean) {
    const id = Number(wallet.id)
    setNotifyOverrides((prev) => ({ ...prev, [id]: next }))
    try {
      const result = await setWalletNotify(id, next)
      if (result.error) throw result.error
      toast.success(next ? t('notify.toastOn') : t('notify.toastOff'))
    } catch (e) {
      // revert on failure
      setNotifyOverrides((prev) => ({ ...prev, [id]: !next }))
      toast.error(apiErrorMessage(e, t('notify.toastError')))
    }
  }

  return (
    <main className="wallets-list-page watched-wallets-page">
      {isLoading && <LoadingSkeleton />}

      {isError && !isLoading && <ErrorState onRetry={refetch} />}

      {!isLoading && !isError && <PageHeader onOpenModal={handleOpenModal} />}

      {removeError && (
        <div className="hint c-danger" role="alert" data-testid="remove-error">
          {removeError}
        </div>
      )}

      {!isLoading && !isError && (!wallets || wallets.length === 0) && (
        <EmptyState onAdd={handleOpenModal} />
      )}

      {!isLoading && !isError && wallets && wallets.length > 0 && filteredWallets.length === 0 && (
        <div className="empty-state">
          <p>{t('empty.noWatchedSearchMatch')}</p>
        </div>
      )}

      {!isLoading && !isError && wallets && wallets.length > 0 && filteredWallets.length > 0 && (
        <>
          <FilterBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
          <section aria-label={t('aria.watchedListSection')}>
            <div className="list" role="list" aria-label={t('aria.watchedList')}>
              {filteredWallets.map((wallet, index) => {
                const id = Number(wallet.id)
                const serverNotify = (wallet as WatchedWalletListItem).notify ?? true
                const notifyOn = notifyOverrides[id] ?? serverNotify
                return (
                  <WatchedWalletRow
                    key={wallet.id}
                    wallet={wallet}
                    index={index}
                    onRemove={handleRemoveClick}
                    notifyOn={notifyOn}
                    onNotifyToggle={handleNotifyToggle}
                  />
                )
              })}
            </div>
          </section>
        </>
      )}

      {!isLoading && !isError && (
        <div className="card card-pad" style={{ marginTop: '16px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            <strong style={{ color: 'var(--text)' }}>{t('footer.watchVsImportTitle')}</strong>{' '}
            <Trans
              i18nKey="footer.watchVsImportBody"
              ns="wallets"
              components={{
                link: (
                  <a
                    href="/wallets"
                    style={{ color: 'var(--primary)' }}
                    onClick={(e) => {
                      e.preventDefault()
                      navigate({ to: '/wallets' })
                    }}
                  />
                ),
              }}
            />
          </div>
        </div>
      )}

      {/* Add Watched Wallet Modal */}
      <AddWatchedWalletModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleAddSuccess}
      />

      {/* Remove-Confirm-Dialog */}
      {removeTarget && (
        <RemoveConfirmDialog
          wallet={removeTarget}
          onConfirm={handleRemoveConfirm}
          onCancel={handleRemoveCancel}
          isLoading={isRemoving}
        />
      )}
    </main>
  )
}